import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = environment.apiUrl;

  constructor(
    private msalService: MsalService,
    private http: HttpClient
  ) {}

  /**
   * Verifica si el usuario está autenticado
   */
  isAuthenticated(): boolean {
    // Verificar autenticación con MSAL (Microsoft)
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      return true;
    }
    
    // Verificar autenticación con email
    const authMethod = localStorage.getItem('authMethod');
    const userEmail = localStorage.getItem('userEmail');
    if (authMethod === 'email' && userEmail) {
      return true;
    }
    
    return false;
  }

  /**
   * Obtiene la cuenta activa del usuario
   */
  getActiveAccount(): AccountInfo | null {
    // Si está autenticado con email desde localStorage
    const authMethod = localStorage.getItem('authMethod');
    if (authMethod === 'email') {
      // Construir un objeto similar a AccountInfo
      return {
        homeAccountId: localStorage.getItem('userId') || '',
        environment: '',
        tenantId: '',
        username: localStorage.getItem('userEmail') || '',
        localAccountId: localStorage.getItem('userId') || '',
        name: localStorage.getItem('userName') || localStorage.getItem('userEmail') || ''
      };
    }
    
    return this.msalService.instance.getActiveAccount();
  }

  /**
   * Obtiene el nombre del usuario autenticado
   */
  getUserName(): string | null {
    // Si está autenticado con email
    const authMethod = localStorage.getItem('authMethod');
    if (authMethod === 'email') {
      return localStorage.getItem('userName') || localStorage.getItem('userEmail');
    }
    
    // Si está autenticado con MSAL
    const account = this.getActiveAccount();
    return account?.name || account?.username || null;
  }

  /**
   * Obtiene el email del usuario autenticado
   */
  getUserEmail(): string | null {
    // Si está autenticado con email
    const authMethod = localStorage.getItem('authMethod');
    if (authMethod === 'email') {
      return localStorage.getItem('userEmail');
    }
    
    // Si está autenticado con MSAL
    const account = this.getActiveAccount();
    return account?.username || null;
  }

  /**
   * Inicia sesión
   */
  login(): void {
    this.msalService.loginRedirect({
      scopes: ['user.read']
    });
  }

  /**
   * Inicia sesión con popup
   */
  loginPopup(): Observable<boolean> {
    return this.msalService.loginPopup({
      scopes: ['user.read']
    }).pipe(
      map(() => {
        this.msalService.instance.setActiveAccount(this.msalService.instance.getAllAccounts()[0]);
        return true;
      })
    );
  }

  /**
   * Cierra sesión
   */
  logout(): void {
    // Si está autenticado con email, limpiar localStorage
    const authMethod = localStorage.getItem('authMethod');
    if (authMethod === 'email') {
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userName');
      localStorage.removeItem('userId');
      localStorage.removeItem('authMethod');
      localStorage.removeItem('isAuthenticated');
      window.location.href = '/';
      return;
    }
    
    // Si está autenticado con MSAL
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: window.location.origin
    });
  }

  /**
   * Cierra sesión con popup
   */
  logoutPopup(): Observable<void> {
    return this.msalService.logoutPopup({
      postLogoutRedirectUri: window.location.origin
    });
  }

  /**
   * Obtiene el ID del usuario (objectId de Azure AD o userId de email)
   */
  getUserId(): string | null {
    // Si está autenticado con email
    const authMethod = localStorage.getItem('authMethod');
    if (authMethod === 'email') {
      return localStorage.getItem('userId');
    }
    
    // Si está autenticado con MSAL
    const account = this.getActiveAccount();
    return account?.localAccountId || account?.homeAccountId || null;
  }

  /**
   * Sincroniza el usuario con la base de datos
   * Registra si no existe, actualiza última conexión si ya existe
   */
  syncUser(): Observable<any> {
    const account = this.getActiveAccount();
    if (!account) {
      return of({ error: 'Usuario no autenticado' });
    }

    const userId = account.localAccountId || account.homeAccountId || account.username;
    const email = account.username || '';
    const name = account.name || '';

    if (!userId || !email) {
      return of({ error: 'Información de usuario incompleta' });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<string>(
      `${this.apiUrl}/api/sync-user`,
      {
        userId: userId,
        email: email,
        name: name
      },
      { 
        headers,
        responseType: 'text' as 'json'
      }
    ).pipe(
      map((response: any) => {
        // Parsear el string JSON
        if (typeof response === 'string') {
          try {
            return JSON.parse(response);
          } catch (e) {
            console.error('Error parseando respuesta de sync-user:', e);
            return { error: 'Respuesta inválida del servidor' };
          }
        }
        return response;
      }),
      catchError(error => {
        console.error('Error sincronizando usuario:', error);
        
        // Si el error tiene un body string, intentar parsearlo
        if (error.error && typeof error.error === 'string') {
          try {
            error.error = JSON.parse(error.error);
          } catch (e) {
            console.error('No se pudo parsear el error:', e);
          }
        }
        
        return of({ error: 'Error al sincronizar usuario' });
      })
    );
  }

  /**
   * Autentica con email
   * Verifica si el email existe en la BD, si existe actualiza fecha de acceso,
   * si no existe lo registra con un uid generado
   */
  loginWithEmail(email: string, password?: string, isNewUser?: boolean): Observable<any> {
    if (!email || !this.isValidEmail(email)) {
      return of({ error: 'Email inválido' });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    const body: any = {
      email: email.toLowerCase().trim()
    };

    // Agregar contraseña e isNewUser si están presentes
    if (password) {
      body.password = password;
    }
    if (isNewUser !== undefined) {
      body.isNewUser = isNewUser;
    }

    return this.http.post<string>(
      `${this.apiUrl}/api/login-email`,
      body,
      { 
        headers,
        responseType: 'text' as 'json'
      }
    ).pipe(
      map((response: any) => {
        // Parsear el string JSON
        if (typeof response === 'string') {
          try {
            return JSON.parse(response);
          } catch (e) {
            console.error('Error parseando respuesta de login-email:', e);
            return { error: 'Respuesta inválida del servidor' };
          }
        }
        return response;
      }),
      catchError(error => {
        console.error('Error en login con email:', error);
        
        // Si el error tiene un body string, intentar parsearlo
        if (error.error && typeof error.error === 'string') {
          try {
            const parsedError = JSON.parse(error.error);
            // Si el error contiene requiresPassword, retornarlo como respuesta exitosa
            if (parsedError.requiresPassword) {
              return of(parsedError);
            }
            error.error = parsedError;
          } catch (e) {
            console.error('No se pudo parsear el error:', e);
          }
        } else if (error.error && error.error.requiresPassword) {
          // Si el error ya está parseado y tiene requiresPassword, retornarlo como respuesta exitosa
          return of(error.error);
        }
        
        // Si hay un error en el objeto error.error, usarlo
        if (error.error && error.error.error) {
          return of(error.error);
        }
        
        return of({ error: 'Error al autenticar con email' });
      })
    );
  }

  /**
   * Valida si un email es válido
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}

